import * as React from 'react';
import { useEffect, useState } from 'react';
import { useFetcher } from '@remix-run/react';
import {
    HoverCard,
    HoverCardContent,
    HoverCardTrigger,
} from '@rocco/components/hover-card';
import { CommentButton } from './CommentButton';
import { CommentPreview } from './CommentPreview';
import { LIST_BUILD_HUB_ITEM_COMMENTS_API_ROUTE } from '~/routes/remix-api.app.build-hub-item-comments.$id';
import type { loader } from '~/routes/remix-api.app.build-hub-item-comments.$id';
import type { Comment } from '@modules/build-hub-comment/types';

interface CommentButtonWithHoverPreviewProps {
    /** ID of the item to fetch comments for */
    itemId: number;
    /** Total number of comments (if > 0, hover preview is enabled) */
    commentsCount?: number;
    /** Number of unread comments */
    unreadCount?: number;
    /** Preloaded comments (if available, skips fetching) */
    preloadedComments?: Comment[];
    /** Whether to render in inline mode */
    inline?: boolean;
    /** Callback when the button is clicked */
    onClick?: (e: React.SyntheticEvent<HTMLElement>) => void;
    /** Additional CSS classes for the button */
    className?: string;
}

/**
 * Enhanced CommentButton with hover preview functionality.
 *
 * This wrapper component adds a Figma-style hover preview popover to the CommentButton.
 * When `commentsCount > 0`, hovering over the button (with 400ms delay) displays a preview
 * showing:
 * - Overlapping user avatars (up to 3, with +X for more)
 * - First (oldest) comment with decoded mentions (@user format)
 * - Total comment count
 *
 * The component eagerly fetches comments on mount (unless preloaded) to eliminate
 * loading states when hovering. This provides a smoother UX at the cost of an
 * additional request per item with comments.
 *
 * Behavior:
 * - Mount: Fetches comments immediately if `commentsCount > 0` (unless preloaded)
 * - Hover: Shows preview popover after 400ms delay (data already loaded)
 * - Click button: Opens detail drawer
 * - Click popover: Opens detail drawer
 * - No comments: Renders standard CommentButton without hover card
 *
 * @example
 * ```tsx
 * <CommentButtonWithHoverPreview
 *   itemId={123}
 *   commentsCount={5}
 *   inline
 *   onClick={handleOpenDrawer}
 * />
 * ```
 *
 * @example With preloaded comments
 * ```tsx
 * <CommentButtonWithHoverPreview
 *   itemId={123}
 *   commentsCount={5}
 *   preloadedComments={comments}
 *   inline
 *   onClick={handleOpenDrawer}
 * />
 * ```
 */
export function CommentButtonWithHoverPreview({
    itemId,
    commentsCount,
    unreadCount,
    preloadedComments,
    inline,
    onClick,
    className,
}: CommentButtonWithHoverPreviewProps) {
    const commentsFetcher = useFetcher<typeof loader>();
    const [hasFetched, setHasFetched] = useState(!!preloadedComments);

    // Eagerly fetch comments on mount if not preloaded
    useEffect(() => {
        if (
            !preloadedComments &&
            !hasFetched &&
            commentsCount &&
            commentsCount > 0
        ) {
            const url = `${LIST_BUILD_HUB_ITEM_COMMENTS_API_ROUTE}/${itemId}`;
            commentsFetcher.load(url);
            setHasFetched(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemId, commentsCount, preloadedComments]);

    // If no comments, render the button without hover card
    if (!commentsCount || commentsCount === 0) {
        return (
            <CommentButton
                itemId={itemId}
                commentsCount={commentsCount}
                unreadCount={unreadCount}
                inline={inline}
                onClick={onClick}
                className={className}
            />
        );
    }

    const comments = preloadedComments ?? commentsFetcher.data?.comments ?? [];
    const isLoading = !preloadedComments && commentsFetcher.state === 'loading';

    const handlePopoverClick = () => {
        if (onClick) {
            // Create a synthetic event to match the expected signature
            const syntheticEvent = {
                stopPropagation: () => {},
                preventDefault: () => {},
            } as React.SyntheticEvent<HTMLElement>;
            onClick(syntheticEvent);
        }
    };

    return (
        <HoverCard openDelay={400} closeDelay={100}>
            <HoverCardTrigger asChild>
                <div>
                    <CommentButton
                        itemId={itemId}
                        commentsCount={commentsCount}
                        unreadCount={unreadCount}
                        inline={inline}
                        onClick={onClick}
                        className={className}
                    />
                </div>
            </HoverCardTrigger>
            <HoverCardContent
                side="top"
                align="start"
                sideOffset={8}
                className="w-auto border-none bg-transparent p-0"
            >
                <CommentPreview
                    comments={comments}
                    commentsCount={commentsCount}
                    isLoading={isLoading}
                    onClick={handlePopoverClick}
                />
            </HoverCardContent>
        </HoverCard>
    );
}
